import React, { useRef, useEffect } from 'react';
import { AlertTriangle, FileText, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { pluralize } from '../../utils/pluralize';

export interface MessinessIssue {
  type: string;
  severity: 'red' | 'amber';
  message: string;
  suggestion: string;
  fixable: boolean;
}

export interface MessinessResult {
  needs_cleanup: boolean;
  confidence: number;
  reasons: string[];
  recommendation: 'cleanup' | 'review' | 'direct';
  file_type: string;
  issues: MessinessIssue[];
  details: Record<string, any>;
  filename: string;
}

interface MessinessCheckModalProps {
  results: MessinessResult[];
  isUploading: boolean;
  onProceedToCleanup: () => void;
  onSkipCleanup: () => void;
  onCancel: () => void;
}

export const MessinessCheckModal: React.FC<MessinessCheckModalProps> = ({
  results,
  isUploading,
  onProceedToCleanup,
  onSkipCleanup,
  onCancel,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, true);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  const hasCleanupRecommendation = results.some(r => r.recommendation === 'cleanup');
  const hasIssues = results.some(r => r.issues.length > 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div ref={modalRef} role="dialog" aria-modal="true" aria-label="Document Quality Check" className="rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        {/* Header */}
        <div className="px-6 py-4" style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}>
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-8 h-8" />
            <div>
              <h2 className="text-xl font-bold">Document Quality Check</h2>
              <p className="text-sm" style={{ color: 'var(--color-on-brand)' }}>We analyzed your files and found some issues</p>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Summary */}
          <div className="mb-6">
            <div className="flex gap-4 text-sm">
              {(() => {
                const redCount = results.filter(r => r.issues.some(i => i.severity === 'red')).length;
                const amberCount = results.filter(r => r.issues.some(i => i.severity === 'amber') && !r.issues.some(i => i.severity === 'red')).length;
                const okCount = results.filter(r => r.issues.length === 0).length;
                return (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--color-brand)' }}></span>
                      <span>{pluralize(redCount, 'file')} {redCount === 1 ? 'needs' : 'need'} attention</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--color-brand)' }}></span>
                      <span>{pluralize(amberCount, 'file')} with warnings</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--color-verified)' }}></span>
                      <span>{pluralize(okCount, 'file')} OK</span>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
          
          {/* File-by-file results */}
          <div className="space-y-4">
            {results.map((result, idx) => (
              <div
                key={idx}
                className="rounded-lg border-2 p-4"
                style={
                  result.issues.some(i => i.severity === 'red')
                    ? { borderColor: 'var(--color-needs-human-tint-bd)', backgroundColor: 'var(--color-needs-human-tint)' }
                    : result.issues.length > 0
                      ? { borderColor: 'var(--color-needs-human-tint-bd)', backgroundColor: 'var(--color-needs-human-tint)' }
                      : { borderColor: 'var(--color-verified)', backgroundColor: 'transparent' }
                }
              >
                {/* File header */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
                    <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{result.filename}</span>
                    <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}>{result.file_type.toUpperCase()}</span>
                  </div>
                  {result.issues.length === 0 ? (
                    <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
                  ) : result.issues.some(i => i.severity === 'red') ? (
                    <XCircle className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                  ) : (
                    <AlertTriangle className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                  )}
                </div>
                
                {/* Issues */}
                {result.issues.length > 0 ? (
                  <ul className="space-y-2">
                    {result.issues.map((issue, iIdx) => (
                      <li key={iIdx} className="text-sm">
                        <div className="flex items-start gap-2">
                          <span className="mt-0.5 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--color-brand)' }}></span>
                          <div>
                            <span style={{ color: issue.severity === 'red' ? 'var(--color-needs-human-text)' : 'var(--color-brand)' }}>
                              {issue.message}
                            </span>
                            <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>{issue.suggestion}</p>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm" style={{ color: 'var(--color-verified)' }}>No issues detected - file looks clean!</p>
                )}
              </div>
            ))}
          </div>
          
          {/* Recommendation */}
          {hasCleanupRecommendation && (
            <div className="mt-6 border rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
              <h3 className="font-semibold mb-2 flex items-center gap-2" style={{ color: 'var(--color-needs-human-text)' }}>
                Recommendation: Use BI Cleanup
              </h3>
              <p className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>
                Some of your files have data quality issues. BI Cleanup will extract, validate, 
                and clean the data with AI assistance and human review before importing.
              </p>
            </div>
          )}
        </div>
        
        {/* Actions */}
        <div className="border-t px-6 py-4 space-y-3" style={{ backgroundColor: 'var(--color-bg-base)' }}>
          {(hasCleanupRecommendation || hasIssues) && (
            <button
              onClick={onProceedToCleanup}
              disabled={isUploading}
              className="w-full px-4 py-3 font-semibold rounded-lg transition-all flex items-center justify-center gap-2"
              style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Uploading to BI Cleanup...
                </>
              ) : (
                <>Use BI Cleanup (Recommended)</>
              )}
            </button>
          )}
          
          <button
            onClick={onSkipCleanup}
            disabled={isUploading}
            className={`w-full px-4 py-3 font-semibold rounded-lg transition-colors ${
              hasCleanupRecommendation || hasIssues
                ? 'border disabled:opacity-50'
                : 'disabled:opacity-50'
            }`}
            style={
              hasCleanupRecommendation || hasIssues
                ? { borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }
                : { backgroundColor: 'var(--color-verified)', color: 'var(--color-on-brand)' }
            }
          >
            {hasCleanupRecommendation || hasIssues
              ? 'Continue Anyway (May have errors)'
              : 'Continue with Upload'}
          </button>

          <button
            onClick={onCancel}
            disabled={isUploading}
            className="w-full px-4 py-2 text-sm disabled:opacity-50"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Cancel and choose different files
          </button>
        </div>
      </div>
    </div>
  );
};

export default MessinessCheckModal;
